import { Card, Button, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useCallback } from 'react';
import { PRE_WORK_CONTEXT } from '../../shared/Routes';
import {
	Batch,
	PreworkCompletionStatusType,
} from '../../shared/VolunteerApplicationServiceTypes';
import { PreworkCompletionStatus } from '../../shared/Constants';
import { useApplicationId } from '../../hooks/useApplicationId';
import ConfirmTick from '../../assets/images/confirm-tick.svg';
import Icon from '../../components/ui-components/Icon';
import moment from 'moment';
import {
	PreworkReviewAction,
	PreworkStatusFunctionaryPredefinedComment,
} from '../../shared/PreWorkServiceTypes';
import { ReApplyAfterSometimeCard } from './ReApplyAfterSometimeCard';

interface ApplicationApprovedCardProps {
	allowed?: boolean;
	pastApplicationId?: number;
	preworkCompletionStatus?: PreworkCompletionStatusType;
	preworkMentorCompletionStatus?: PreworkCompletionStatusType;
	currentBatch?: Batch;
	approverPreworkRecommendation?: PreworkReviewAction;
	isPreworkStautsRejectedByApprover?: boolean;
	preworkStatusFunctionaryPredefinedComments?: Array<PreworkStatusFunctionaryPredefinedComment>;
	reapplyDateForRejectedPrework?: string;
	pastApplicationBatch?: Batch;
}

export const ApplicationApprovedCard = ({
	allowed,
	pastApplicationId,
	preworkCompletionStatus,
	preworkMentorCompletionStatus,
	currentBatch,
	approverPreworkRecommendation,
	isPreworkStautsRejectedByApprover,
	preworkStatusFunctionaryPredefinedComments,
	reapplyDateForRejectedPrework,
	pastApplicationBatch,
}: ApplicationApprovedCardProps) => {
	const { t, i18n } = useTranslation();
	const { mySrcmProfile } = useAuth();
	const { navigateWithApplicationId } = useApplicationId();
	const candidateName = mySrcmProfile?.name;
	const showProceedToPreWorkButton =
		preworkCompletionStatus !== PreworkCompletionStatus.COMPLETED;
	const batchName = pastApplicationBatch?.name;
	const snpCandidateResultDate =
		pastApplicationBatch?.snpResultDateForCandidate;
	const isResultAfter90Days = moment().isAfter(
		moment(snpCandidateResultDate).add(90, 'days')
	);
	const isLengthyLanguage = ['ta', 'ma', 'ka'].includes(i18n.language);

	const handleClickToProceedClick = useCallback(() => {
		navigateWithApplicationId(PRE_WORK_CONTEXT, pastApplicationId);
	}, [navigateWithApplicationId, pastApplicationId]);

	const renderCard = (title: string, message: string, showButton = false) => (
		<Card className="w-full md:w-[50%] mx-auto px-6 font-sans">
			<div className="flex flex-col items-center text-center space-y-4">
				<div className="text-primary-green font-semibold text-lg">{title}</div>
				<Divider />
				<Icon src={ConfirmTick} className="!w-24 !h-24" />
				<div className="space-y-2">
					<p>{t('dearCandidateName', { candidateName })}</p>
					<p>{message}</p>
					<p>
						{t('heartfulRespectsAndRegards')}
						<br />
						{t('snpTeam')}
					</p>
				</div>
			</div>
			{showButton && (
				<div className="flex justify-center mt-6">
					<Button
						type="primary"
						onClick={handleClickToProceedClick}
						className={isLengthyLanguage ? 'text-[12px] sm:text-[14px]' : ''}
					>
						{t('clickToProceed')}
					</Button>
				</div>
			)}
		</Card>
	);

	if (
		preworkMentorCompletionStatus !== 'COMPLETED' &&
		approverPreworkRecommendation !== PreworkReviewAction.PREWORK_APPROVE &&
		isResultAfter90Days
	) {
		return renderCard(
			t('applyNextBatch'),
			t('applicationIsClosed', { batchName })
		);
	}

	if (
		preworkMentorCompletionStatus === 'COMPLETED' ||
		approverPreworkRecommendation === PreworkReviewAction.PREWORK_APPROVE
	) {
		return renderCard(
			t('preworkConfirmationCompleted', { batchName }),
			t('yourScreeningResultIsApprove', { batchName })
		);
	}

	if (
		isPreworkStautsRejectedByApprover &&
		approverPreworkRecommendation === PreworkReviewAction.PREWORK_REJECT
	) {
		return (
			<ReApplyAfterSometimeCard
				allowed={allowed}
				reApplyDate={reapplyDateForRejectedPrework}
				preworkStatusFunctionaryPredefinedComments={
					preworkStatusFunctionaryPredefinedComments
				}
				pastApplicationBatch={pastApplicationBatch}
				currentBatch={currentBatch}
			/>
		);
	}

	if (
		preworkMentorCompletionStatus === 'PARTIALLY_COMPLETED' ||
		preworkMentorCompletionStatus === 'INCOMPLETE'
	) {
		return renderCard(
			t('preworkIncomplete', { batchName }),
			t('recommendedForExtendedPreWork'),
			showProceedToPreWorkButton
		);
	}

	if (preworkCompletionStatus === 'COMPLETED') {
		return renderCard(
			t('preworkSubmitted'),
			t('yourPreworkSubmitted', { batchName })
		);
	}

	if (
		preworkCompletionStatus === 'PARTIALLY_COMPLETED' ||
		preworkCompletionStatus === 'INCOMPLETE'
	) {
		return renderCard(
			t('preworkIncomplete', { batchName }),
			t('onceYouComplete'),
			showProceedToPreWorkButton
		);
	}

	return renderCard(
		t('proceedToPrework'),
		t('yourApplicationIsUnderReview', { batchName }),
		showProceedToPreWorkButton
	);
};
